import React, { useCallback, useEffect, useState } from "react";
import {
  Trophy, Coins, Flame, Gift, Users, BookOpenCheck, Copy, Check, Crown,
  Sparkles, Newspaper, Palette, Store, Medal, History, ArrowRight, Link2, Zap, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useEntitlements } from "../context/EntitlementsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString();

const STORE_ICON = { crown: Crown, sparkles: Sparkles, newspaper: Newspaper, palette: Palette };
const LEDGER_LABEL = {
  daily: "Daily sign-in", streak_bonus: "Streak bonus", journal: "Journaling",
  referral_joined: "Referral bonus", referral_reward: "Friend joined", redeem: "Redeemed", mt5: "MT5 connected",
};

const Card = ({ children, className = "", delay = 0 }) => (
  <div className={`ws-panel p-6 ws-fade-up ${className}`} style={{ animationDelay: `${delay}ms` }}>{children}</div>
);

const GrowthZone = () => {
  const { ent, refresh: refreshEnt } = useEntitlements();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/growth/summary`, { credentials: "include" }),
        fetch(`${API}/growth/leaderboard`, { credentials: "include" }),
      ]);
      if (s.ok) setData(await s.json());
      if (l.ok) setBoard((await l.json()).leaderboard || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    const res = await fetch(`${API}/growth/${path}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.detail || "Something went wrong");
    return j;
  };

  const claimDaily = async () => {
    setBusy("daily");
    try {
      const r = await post("claim-daily");
      toast.success(`+${r.awarded} WS Coins claimed!${r.bonus ? ` (incl. ${r.bonus} streak bonus)` : ""}`);
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const applyReferral = async () => {
    if (!refInput.trim()) return;
    setBusy("ref");
    try {
      const r = await post("apply-referral", { code: refInput.trim() });
      toast.success(`Referral applied! +${r.awarded} WS Coins`);
      setRefInput("");
      await load();
      refreshEnt && refreshEnt();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const redeem = async (item) => {
    setBusy(item.id);
    try {
      const r = await post("redeem", { item_id: item.id });
      toast.success(`Redeemed: ${r.redeemed}`);
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const copyRef = () => {
    const link = `${window.location.origin}/?ref=${data.referral.code}`;
    navigator.clipboard?.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  if (!data) return <div className="ws-panel p-10 m-8 text-center text-[color:var(--ws-muted)]" data-testid="growth-loading">Loading Growth Zone…</div>;

  const { balance, lifetime, rank, daily, referral, journal, tasks, store, ledger } = data;
  const jPct = Math.min(100, Math.round((journal.coins_today / journal.daily_cap) * 100));
  const demo = ent ? ent.demo : false;
  const refCount = ent ? ent.referrals : (referral?.count || 0);
  const MILESTONES = [
    { count: 10, label: "WS News Pro", days: 10, icon: Newspaper },
    { count: 15, label: "VIP Community", days: 5, icon: Users },
    { count: 20, label: "WS VIP Community", days: 10, icon: Crown },
    { count: 50, label: "Full Journal", days: 15, icon: BookOpenCheck },
  ];

  return (
    <div className="max-w-[1180px] mx-auto px-8 py-8" data-testid="growth-zone">
      <div className="flex items-center gap-3 mb-2 ws-fade-up">
        <div className="ws-icon-chip w-9 h-9"><Trophy className="w-5 h-5" /></div>
        <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Earn · Rank Up · Redeem</span>
      </div>
      <h1 className="ws-heading text-[40px] font-bold mb-6 ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>Growth Zone</h1>

      {/* Coin hero */}
      <div className="ws-coin-hero ws-fade-up mb-5" style={{ animationDelay: "60ms" }} data-testid="coin-hero">
        <div className="ws-coin-hero-glow" aria-hidden />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="flex items-center gap-5">
            <div className="ws-coin-badge"><Coins className="w-8 h-8" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--ws-muted)]">WS Coins Balance</div>
              <div className="ws-coin-balance" data-testid="coin-balance">{fmt(balance)}</div>
              <div className="text-[12px] text-[color:var(--ws-muted)] mt-1">Lifetime earned · <span className="text-[color:var(--ws-cyan)] font-semibold">{fmt(lifetime)}</span></div>
            </div>
          </div>
          <div className="md:w-[340px]">
            <div className="flex items-center justify-between mb-2">
              <span className="ws-rank-chip" style={{ color: rank.color, borderColor: `${rank.color}66`, background: `${rank.color}18` }} data-testid="coin-rank">
                <Medal className="w-3.5 h-3.5" /> {rank.name}
              </span>
              <span className="text-[11px] text-[color:var(--ws-muted)]">{rank.next ? `Next: ${rank.next} · ${fmt(rank.next_at)}` : "Max rank"}</span>
            </div>
            <div className="h-2.5 rounded-full bg-[rgba(120,140,180,0.16)] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${rank.progress}%`, background: `linear-gradient(90deg,${rank.color},#e6c25a)` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Earn cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        {/* Daily sign-in */}
        <Card delay={80}>
          <div className="flex items-center gap-2 mb-3"><Flame className="w-4 h-4 text-[#ff9d4d]" /><h3 className="ws-heading text-[15px] font-bold">Daily Sign-in</h3></div>
          <div className="flex items-center gap-2 mb-4" data-testid="streak-dots">
            {Array.from({ length: 7 }).map((_, i) => (
              <span key={i} className={`ws-streak-dot ${i < (daily.streak % 7 || (daily.claimed_today ? 7 : 0)) ? "on" : ""}`}>{i + 1}</span>
            ))}
          </div>
          <p className="text-[12px] text-[color:var(--ws-muted)] mb-4">
            <b className="text-white">{daily.amount} coins</b> every day · <b className="text-[color:var(--ws-cyan)]">+{daily.streak_bonus}</b> on every {daily.streak_bonus_every}-day streak. Current streak: <b className="text-white">{daily.streak}🔥</b>
          </p>
          <button onClick={claimDaily} disabled={demo || daily.claimed_today || busy === "daily"}
            className={`w-full py-2.5 rounded-xl text-[13px] font-semibold transition-all ${(demo || daily.claimed_today) ? "ws-btn-ghost opacity-60 cursor-not-allowed" : "ws-btn-primary"}`}
            data-testid="claim-daily-btn">
            {demo ? "🔒 Connect a broker to earn" : daily.claimed_today ? "Claimed today ✓" : busy === "daily" ? "Claiming…" : `Claim +${daily.amount} coins`}
          </button>
        </Card>

        {/* Journal time */}
        <Card delay={120}>
          <div className="flex items-center gap-2 mb-3"><BookOpenCheck className="w-4 h-4 text-[color:var(--ws-cyan)]" /><h3 className="ws-heading text-[15px] font-bold">Journaling Time</h3></div>
          <p className="text-[12px] text-[color:var(--ws-muted)] mb-3">Earn <b className="text-white">{journal.per_min} coins</b> per active minute in the AI Journal — up to <b className="text-white">{journal.daily_cap}</b>/day.</p>
          <div className="flex items-end justify-between mb-1">
            <span className="ws-heading text-[22px] font-bold text-white" data-testid="journal-coins-today">{fmt(journal.coins_today)}<span className="text-[12px] text-[color:var(--ws-muted)] font-normal"> / {journal.daily_cap}</span></span>
            <span className="text-[11px] text-[color:var(--ws-muted)]">{journal.minutes_today} min today</span>
          </div>
          <div className="h-2.5 rounded-full bg-[rgba(120,140,180,0.16)] overflow-hidden mb-4">
            <div className="h-full rounded-full" style={{ width: `${jPct}%`, background: "linear-gradient(90deg,#38bdf8,#2563eb)" }} />
          </div>
          <a href="/ai-journal" className="w-full ws-btn-ghost py-2.5 rounded-xl text-[13px] font-semibold inline-flex items-center justify-center gap-2" data-testid="go-journal-btn">
            <Zap className="w-3.5 h-3.5" /> Open Journal & earn
          </a>
        </Card>

        {/* Referral */}
        <Card delay={160}>
          <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-[#e6c25a]" /><h3 className="ws-heading text-[15px] font-bold">Refer & Earn</h3></div>
          <p className="text-[12px] text-[color:var(--ws-muted)] mb-3">Share your link — you get <b className="text-white">+{referral.reward_referrer}</b>, your friend gets <b className="text-white">+{referral.reward_referred}</b>.</p>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 ws-price-cell px-3 py-2 rounded-lg flex items-center gap-2 min-w-0">
              <Link2 className="w-3.5 h-3.5 text-[color:var(--ws-muted)] shrink-0" />
              <span className="text-[13px] font-semibold text-white truncate" data-testid="referral-code">{referral.code}</span>
            </div>
            <button onClick={copyRef} className="ws-btn-primary px-3 py-2 rounded-lg" data-testid="copy-referral-btn" title="Copy link">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center justify-between text-[11px] text-[color:var(--ws-muted)] mb-3">
            <span><b className="text-[color:var(--ws-cyan)]">{referral.count}</b> friends joined</span>
          </div>
          {!referral.applied && (
            <div className="flex items-center gap-2">
              <input value={refInput} onChange={(e) => setRefInput(e.target.value.toUpperCase())} placeholder="Have a code?"
                className="flex-1 bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-lg text-[12px] text-white px-2.5 py-2 outline-none" data-testid="referral-input" />
              <button onClick={applyReferral} disabled={busy === "ref"} className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-semibold" data-testid="apply-referral-btn">Apply</button>
            </div>
          )}
        </Card>
      </div>

      {/* Referral Rewards — unlock premium access by referring (the demo path) */}
      <div className="ws-panel p-6 mb-5 ws-fade-up ws-refmiles" style={{ animationDelay: "170ms" }} data-testid="referral-rewards">
        <div className="flex items-center gap-2 mb-1">
          <div className="ws-icon-chip w-8 h-8"><Gift className="w-4 h-4" /></div>
          <h3 className="ws-heading text-[16px] font-semibold">Unlock Access by Referring</h3>
        </div>
        <p className="text-[12px] text-[color:var(--ws-muted)] mb-5">
          {demo
            ? "You're in demo mode — every feature is locked. Connect a broker for full free access, or invite friends to unlock premium features for a limited time."
            : "You have full access. Referral rewards still stack up as a bonus!"}
          <span className="text-white font-semibold"> {refCount} friends joined so far.</span>
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="milestones">
          {MILESTONES.map((m) => {
            const reached = refCount >= m.count;
            const pct = Math.min(100, Math.round((refCount / m.count) * 100));
            const Icon = m.icon;
            return (
              <div key={`${m.count}-${m.label}`} className={`ws-milestone ${reached ? "is-on" : ""}`} data-testid={`milestone-${m.count}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${reached ? "bg-[rgba(52,211,153,0.16)] ws-up" : "bg-[rgba(59,130,246,0.12)] text-[color:var(--ws-blue-bright)]"}`}>
                    {reached ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="ws-milestone-count">{m.count} refs</span>
                </div>
                <div className="ws-heading text-[14px] font-bold text-white">{m.label}</div>
                <div className="text-[11.5px] text-[color:var(--ws-muted)] mb-3">{m.days} days free access</div>
                <div className="h-2 rounded-full bg-[rgba(120,140,180,0.16)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: reached ? "linear-gradient(90deg,#34d399,#38bdf8)" : "linear-gradient(90deg,#2563eb,#38bdf8)" }} />
                </div>
                <div className="text-[10.5px] mt-1.5 font-semibold" style={{ color: reached ? "#34d399" : "var(--ws-muted)" }}>
                  {reached ? "Unlocked ✓" : `${refCount}/${m.count}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quests */}
      <Card delay={180} className="mb-5">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[color:var(--ws-blue-bright)]" /><h3 className="ws-heading text-[16px] font-semibold">Daily Quests</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {tasks.map((t) => (
            <div key={t.id} className="ws-price-cell p-3.5 rounded-xl flex items-center gap-3" data-testid={`quest-${t.id}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.done ? "bg-[rgba(52,211,153,0.14)]" : "bg-[rgba(59,130,246,0.12)]"}`}>
                {t.done ? <Check className="w-4 h-4 ws-up" /> : <Coins className="w-4 h-4 text-[#e6c25a]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-white">{t.label}</div>
                {t.target != null
                  ? <div className="text-[11px] text-[color:var(--ws-muted)]">{fmt(t.progress)} / {fmt(t.target)} coins today</div>
                  : <div className="text-[11px] text-[color:var(--ws-muted)]">Reward: +{fmt(t.reward)} coins</div>}
              </div>
              <span className={`text-[11px] font-semibold ${t.done ? "ws-up" : "text-[#e6c25a]"}`}>{t.done ? "Done" : `+${fmt(t.reward)}`}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Store */}
      <Card delay={200} className="mb-5">
        <div className="flex items-center gap-2 mb-1"><Store className="w-4 h-4 text-[#e6c25a]" /><h3 className="ws-heading text-[16px] font-semibold">Redeem Store</h3></div>
        <p className="text-[12px] text-[color:var(--ws-muted)] mb-4">Spend your WS Coins on premium perks.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="redeem-store">
          {store.map((item) => {
            const Icon = STORE_ICON[item.icon] || Gift;
            const afford = balance >= item.cost && !demo;
            return (
              <div key={item.id} className="ws-store-card" data-testid={`store-${item.id}`}>
                <div className="ws-store-ic"><Icon className="w-5 h-5" /></div>
                <div className="ws-heading text-[14px] font-bold text-white mt-3">{item.title}</div>
                <p className="text-[11.5px] text-[color:var(--ws-muted)] mt-1 flex-1">{item.desc}</p>
                <div className="flex items-center gap-1.5 mt-3 mb-3"><Coins className="w-4 h-4 text-[#e6c25a]" /><span className="ws-heading text-[16px] font-bold text-white">{fmt(item.cost)}</span></div>
                <button onClick={() => redeem(item)} disabled={!afford || busy === item.id}
                  className={`w-full py-2 rounded-lg text-[12px] font-semibold transition-all ${afford ? "ws-btn-primary" : "ws-btn-ghost opacity-50 cursor-not-allowed"}`}
                  data-testid={`redeem-${item.id}`}>
                  {busy === item.id ? "Redeeming…" : demo ? "🔒 Locked in demo" : afford ? "Redeem" : "Not enough"}
                </button>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Leaderboard + History */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card delay={220}>
          <div className="flex items-center gap-2 mb-4"><Trophy className="w-4 h-4 text-[#e6c25a]" /><h3 className="ws-heading text-[16px] font-semibold">Leaderboard</h3></div>
          <div className="space-y-2" data-testid="leaderboard">
            {board.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">No earners yet — be the first!</div>}
            {board.map((u) => (
              <div key={u.rank} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${u.you ? "ws-lb-you" : "ws-price-cell"}`}>
                <span className={`ws-lb-rank ${u.rank <= 3 ? `top${u.rank}` : ""}`}>{u.rank}</span>
                {u.picture
                  ? <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full border border-[color:var(--ws-border-strong)]" />
                  : <div className="w-8 h-8 rounded-full bg-[rgba(59,130,246,0.2)] flex items-center justify-center text-[12px] font-bold text-[color:var(--ws-blue-bright)]">{u.name?.[0]?.toUpperCase() || "U"}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-white truncate">{u.name}{u.you && <span className="text-[10px] text-[color:var(--ws-blue-bright)] ml-1.5">YOU</span>}</div>
                  <div className="text-[11px] text-[color:var(--ws-muted)]">{u.tier}</div>
                </div>
                <span className="flex items-center gap-1 text-[13px] font-bold text-[#e6c25a]"><Coins className="w-3.5 h-3.5" />{fmt(u.coins)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card delay={260}>
          <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-[color:var(--ws-cyan)]" /><h3 className="ws-heading text-[16px] font-semibold">Coin History</h3></div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="coin-history">
            {ledger.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">No activity yet. Claim your daily coins to start!</div>}
            {ledger.map((l, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg ws-price-cell">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-white truncate">{LEDGER_LABEL[l.type] || l.type}</div>
                  <div className="text-[10px] text-[color:var(--ws-muted)]">{l.ts ? new Date(l.ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                </div>
                <span className={`text-[13px] font-bold ${l.amount >= 0 ? "ws-up" : "ws-down"}`}>{l.amount >= 0 ? "+" : ""}{fmt(l.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GrowthZone;
